import { useContext } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';

import { contentOptimizerPathes } from '../../../../../constants/queryPathes';
import {
    defaultExtractContentEditorContent,
    editorTypes,
} from '../../constants/tiptapEditorConstants';

import { ContentOptimizerContext } from '../../../../hocs/ContentOptimizerProvider';

import { axiosAPI } from '../../../../../utils/axiosAPI';
import { getAxiosHeaders } from '../../../../../utils/helpers/getAxiosHeaders';
import { useOrganizationSlug } from '../../../../../utils/hooks/useOrganizationSlug';

import {
    updateCurrentEditorContent,
    updateCurrentEditorText,
    updateEditorBasicData,
    updateEditorCurrentVersionScore,
    updateEditorCurrentVersionWordCount,
    updateSaveEditorLoading,
} from '../../store/tiptapEditor.actions';
import {
    selectCurrentEditorId,
    selectEditorPreviewVersion,
} from '../../store/tiptapEditor.selectors';

const editorType = editorTypes.OPTIMIZER;

const useUpdateOptimizerEditorContent = () => {
    const dispatch = useDispatch();

    const contentOptimizerId = useContext(ContentOptimizerContext);

    const organizationSlug = useOrganizationSlug();

    const currentEditorId = useSelector(selectCurrentEditorId(editorType));
    const editorPreviewVersion = useSelector(selectEditorPreviewVersion(editorType));

    const handleUpdateEditor = (
        updatedEditorData,
        updatedEditorText = '',
        addToExistingContent = false
    ) => {
        if (contentOptimizerId && currentEditorId && !editorPreviewVersion) {
            dispatch(updateSaveEditorLoading({ editorType, loading: true }));
            dispatch(updateCurrentEditorText({ editorType, text: updatedEditorText }));
            const dataToUpdate = updatedEditorData || defaultExtractContentEditorContent;

            axiosAPI
                .patch(
                    `${contentOptimizerPathes.getContentOptimizers}/${contentOptimizerId}/versions/${currentEditorId}?add_to_existing=${addToExistingContent}`,
                    {
                        content: JSON.stringify(dataToUpdate),
                        content_text: updatedEditorText,
                    },
                    {
                        ...getAxiosHeaders(),
                        params: {
                            slug: organizationSlug,
                        },
                    }
                )
                .then((result) => {
                    // No need to update those data if the Editor page is not active
                    if (!addToExistingContent) {
                        dispatch(updateCurrentEditorContent({ editorType, content: dataToUpdate }));

                        if (result?.data) {
                            batch(() => {
                                dispatch(updateEditorBasicData(result.data));

                                const { rankability_score, word_count } = result.data;
                                dispatch(
                                    updateEditorCurrentVersionWordCount({
                                        editorType,
                                        wordCount: word_count,
                                    })
                                );
                                dispatch(
                                    updateEditorCurrentVersionScore({
                                        editorType,
                                        score: rankability_score,
                                    })
                                );
                            });
                        }
                    }
                })
                .finally(() => {
                    dispatch(updateSaveEditorLoading({ editorType, loading: false }));
                });
        }
    };

    return handleUpdateEditor;
};

export default useUpdateOptimizerEditorContent;
